import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  IonRow,
  IonCol,
  useIonAlert,
  IonButton,
  IonList,
  IonItem,
  IonAvatar,
  IonLabel,
  IonIcon,
  useIonLoading,
  useIonToast,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonContent,
  IonInput,
  IonFooter,
} from "@ionic/react";
import { useHistory } from "react-router-dom";
import { BadgeContext } from "../../context/BadgeContext";
import { ProfileContext } from "../../context/ProfileContext";
import { SchedJobContext } from "../../context/JobContextScheduler";
import {
  PUSH_NOTIFICATION_TYPES,
  VIEW_TYPES,
  WORKFLOW_SCHEDULER_TYPES,
  WORKFLOW_WERKER_TYPES,
  WORKFLOW_FEED_TYPES,
  FB_COLLECTION_PATH,
} from "../../functions/postActionTypes";
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { format } from "date-fns";
import {
  cashOutline,
  closeOutline,
  personAdd,
  chevronDownOutline,
  chevronForwardOutline,
  helpCircleOutline,
  saveOutline,
  addOutline,
  trashOutline,
} from "ionicons/icons";
import { fx_FB_SendNotification } from "../../functions/Fx_SendPushNotification";
import { fx_FB_WerkerFeedUpdateAll } from "../../functions/fx_FB_WerkerFeedUpdateAll";
import { UnpaidWerkerSummary, UnpaidJobData } from "../../types/payment";
import { calculateJobPay } from "../../utils/paymentCalculations";
import { findPaymentReceiptForJob } from "../../functions/Fx_Payment";

interface SchedWerkersComponentProps {
  jobDocId: string;
  seriesId?: string | null;
  onEditWerkersRequest?: (result: {
    nextView: "selectWerkers" | "selectWerkersSeries";
    jobId?: string;
    seriesId?: string;
  }) => void;
}

export const SchedWerkersComponent: React.FC<SchedWerkersComponentProps> = ({
  jobDocId,
  seriesId,
  onEditWerkersRequest,
}) => {
  const { schedJob } = useContext(SchedJobContext);
  const { badgeDispatch } = useContext(BadgeContext);
  const { usersID, profile } = useContext(ProfileContext);
  const history = useHistory();
  const [present] = useIonAlert();
  const [presentLoading, dismissLoading] = useIonLoading();
  const [presentToast] = useIonToast();

  // ✅ Invited collapsed by default
  const [invitedOpen, setInvitedOpen] = useState<boolean>(false);
  const [jobReady, setJobReady] = useState<boolean>(false);

  // Dummy werker modal state
  const [showAddWerkerModal, setShowAddWerkerModal] = useState<boolean>(false);
  const [showDummyModal, setShowDummyModal] = useState<boolean>(false);
  const [dummyFirstName, setDummyFirstName] = useState<string>("Dummy");
  const [dummyLastName, setDummyLastName] = useState<string>("Werker");

  interface WerkerJobData {
    JobDocId: string;
    MyJobDocId: string;
    ShiftIdentifier: string;
    WerkerUid: string;
    DateDay: string;
    StartDateTime: string;
    FinishDateTime: string;
    StartDateTimeRaw: string;
    FinishDateTimeRaw: string;
    Company: string;
    Location: string;
    Pay: string;
    ShiftNotes: string;
    NumJobSlots: number;
    JobStatusSched: string;
    JobStatusWerker: string;
    IsPaidJob: boolean;
    PaidWerker: boolean;
    PaidWerkers: boolean;
    JobCancelledByScheduler: boolean;
    CreatedAt: number;
    UpdatedAt: number;
    IsRead: boolean;
    IsNewMessage: boolean;
    WerkerFirstName: string;
    WerkerLastName: string;
    WerkerProfilePic: string;
    WerkerStatus: string;
    DocPath: string;
  }

  const [myWerkersJob, setMyWerkersJob] = useState<WerkerJobData[]>([]);

  interface DummyWerkerData {
    id: string;
    firstName: string;
    lastName: string;
    createdAt: number;
  }

  interface SavedDummyData {
    id: string;
    firstName: string;
    lastName: string;
    createdAt: number;
  }

  const [dummyWerkersForJob, setDummyWerkersForJob] = useState<DummyWerkerData[]>([]);
  const [savedDummies, setSavedDummies] = useState<SavedDummyData[]>([]);

  interface SchedJobData {
    JobDocId: string;
    ShiftIdentifier: string;
    SchedUid: string;
    DateDay: string;
    StartDateTime: string;
    FinishDateTime: string;
    StartDateTimeRaw: string;
    FinishDateTimeRaw: string;
    Company: string;
    Location: string;
    Pay: string;
    ShiftNotes: string;
    NumJobSlots: number;
    JobStatusSched: string;
    JobStatusWerker: string;
    IsPaidJob: boolean;
    PaidWerker: boolean;
    PaidWerkers: boolean;
    JobCancelledByScheduler: boolean;
    CreatedAt: number;
    UpdatedAt: number;
    IsRead: boolean;
    IsNewMessage: boolean;
  }

  const emptySchedJob: SchedJobData = {
    JobDocId: "",
    ShiftIdentifier: "",
    SchedUid: "",
    DateDay: "",
    StartDateTime: "",
    FinishDateTime: "",
    StartDateTimeRaw: "",
    FinishDateTimeRaw: "",
    Company: "",
    Location: "",
    Pay: "",
    ShiftNotes: "",
    NumJobSlots: 0,
    JobStatusSched: "",
    JobStatusWerker: "",
    IsPaidJob: false,
    PaidWerker: false,
    PaidWerkers: false,
    JobCancelledByScheduler: false,
    CreatedAt: 0,
    UpdatedAt: 0,
    IsRead: false,
    IsNewMessage: false,
  };

  const [currentSchedJob, setCurrentSchedJob] = useState<SchedJobData>(emptySchedJob);

  // Ensure scheduledCount exists for legacy jobs (scheduler job doc: jobsDocs/{jobDocId})
  const ensureScheduledCountExists = async (jobDocRef: any, uniqueJobDocId: string): Promise<void> => {
    const snap = await getDoc(jobDocRef);
    if (!snap.exists()) {
      throw new Error("Job no longer exists.");
    }

    const data: any = snap.data();
    const existing = data?.jobInfo?.status?.scheduledCount;

    if (existing !== undefined) return;

    const q = query(
      collectionGroup(db, "jobsDocs"),
      where("jobInfo.core.uniqueJobId", "==", uniqueJobDocId),
      where("jobInfo.status.jobStatusWerker", "in", [WORKFLOW_WERKER_TYPES.Scheduled, WORKFLOW_WERKER_TYPES.Past])
    );

    const derivedSnap = await getDocs(q);

    await updateDoc(jobDocRef, {
      "jobInfo.status.scheduledCount": derivedSnap.size,
    });
  };

  // -----------------------------
  // Feed helpers (NEW)
  // users/{werkerUid}/jobFeed/{jobDocId}
  // -----------------------------

  // ✅ createdAt is write-once (heal if missing). Source of truth is jobFeed doc itself.
  const upsertFeedWriteOnceCreatedAt = async (werkerUid: string, jobId: string, payload: any) => {
    if (!werkerUid || !jobId) return;

    const feedRef = doc(db, FB_COLLECTION_PATH.Users, werkerUid, FB_COLLECTION_PATH.JobFeed, jobId);
    const now = Date.now();

    const snap = await getDoc(feedRef);
    const existing: any = snap.exists() ? snap.data() : null;

    const finalPayload: any = {
      ...payload,
      updatedAt: now,
    };

    if (!existing?.createdAt) {
      finalPayload.createdAt = now;
    }

    await setDoc(feedRef, finalPayload, { merge: true });
  };

  const buildFeedSnapshot = (overrides?: any) => {
    return {
      jobId: jobDocId,
      werkerJobDocId: overrides?.werkerJobDocId ?? "",
      schedUid: usersID,
      status: overrides?.status ?? WORKFLOW_FEED_TYPES.Scheduled,

      company: currentSchedJob?.Company ?? "",
      location: currentSchedJob?.Location ?? "",
      pay: currentSchedJob?.Pay ?? "",
      shiftIdentifier: currentSchedJob?.ShiftIdentifier ?? "",
      shiftNotes: currentSchedJob?.ShiftNotes ?? "",
      startDateTimeRaw: currentSchedJob?.StartDateTimeRaw ?? "",
      finishDateTimeRaw: currentSchedJob?.FinishDateTimeRaw ?? "",

      schedFirstName: profile?.FirstName ?? "",
      schedLastName: profile?.LastName ?? "",
      schedProfilePicUrl: profile?.ProfilePicURL ?? "",

      isRead: overrides?.isRead ?? false,
      isNewMessage: overrides?.isNewMessage ?? false,
      dismissed: overrides?.dismissed ?? false,
      dismissedAt: overrides?.dismissedAt ?? null,

      ...overrides,
    };
  };

  const deleteFeedForWerker = async (werkerUid: string) => {
    if (!werkerUid) return;
    const feedRef = doc(db, FB_COLLECTION_PATH.Users, werkerUid, FB_COLLECTION_PATH.JobFeed, jobDocId);
    await deleteDoc(feedRef).catch(() => {});
  };

  const setFeedScheduledForWerker = async (werkerUid: string, werkerJobDocId: string) => {
    if (!werkerUid) return;

    await upsertFeedWriteOnceCreatedAt(
      werkerUid,
      jobDocId,
      buildFeedSnapshot({
        werkerJobDocId,
        status: WORKFLOW_FEED_TYPES.Scheduled,
        dismissed: false,
        dismissedAt: null,
        isRead: false,
        isNewMessage: false,
      })
    );
  };

  const setFeedAvailableForWerker = async (werkerUid: string, werkerJobDocId: string) => {
    if (!werkerUid) return;

    await upsertFeedWriteOnceCreatedAt(
      werkerUid,
      jobDocId,
      buildFeedSnapshot({
        werkerJobDocId,
        status: "available",
        dismissed: false,
        dismissedAt: null,
        isRead: false,
        isNewMessage: false,
      })
    );
  };

  const getUidFromWerkerJobDoc = (data: any): string => {
    return data?.docStamp?.userInfoTo?.uid || data?.docStamp?.userInfoTo?.UserId || "";
  };

  useEffect(
    () => {
      const matchedJob = Array.isArray(schedJob) ? schedJob.find((sj: any) => sj.JobDocId === jobDocId) : undefined;

      if (matchedJob) {
        setCurrentSchedJob(matchedJob);
        setJobReady(true);
      } else {
        setCurrentSchedJob(emptySchedJob);
        setJobReady(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobDocId, schedJob]
  );

  useEffect(() => {
    const werkerJobsListQuery = query(
      collectionGroup(db, "jobsDocs"),
      where("jobInfo.core.uniqueJobId", "==", jobDocId),
      where("jobInfo.status.jobStatusWerker", "in", [
        WORKFLOW_WERKER_TYPES.Invited,
        WORKFLOW_WERKER_TYPES.Scheduled,
        WORKFLOW_WERKER_TYPES.Past,
        WORKFLOW_WERKER_TYPES.Uninvited,
        WORKFLOW_WERKER_TYPES.Expired,
        WORKFLOW_WERKER_TYPES.Fired,
        WORKFLOW_WERKER_TYPES.Cancelled,
        WORKFLOW_WERKER_TYPES.Deleted,
      ])
    );

    try {
      const unsubscribe = onSnapshot(werkerJobsListQuery, (querySnapshot) => {
        if (querySnapshot.empty) {
          setMyWerkersJob([]);
          return;
        }

        const werkerJobDocs: WerkerJobData[] = [];

        querySnapshot.forEach((docu) => {
          const jobDoc: any = docu.data();
          werkerJobDocs.push({
            JobDocId: jobDoc.jobInfo.core.uniqueJobId,
            MyJobDocId: docu.id,
            ShiftIdentifier: jobDoc.jobInfo.core.shiftIdentifier || "",
            WerkerUid: jobDoc.docStamp.userInfoTo.uid,
            DateDay: format(new Date(jobDoc.jobInfo.core.dateTimeStart), "M/d/yy") || "",
            StartDateTime: format(new Date(jobDoc.jobInfo.core.dateTimeStart), "h:mma") || "",
            FinishDateTime: format(new Date(jobDoc.jobInfo.core.dateTimeFinish), "h:mma") || "",
            StartDateTimeRaw: jobDoc.jobInfo.core.dateTimeStart,
            FinishDateTimeRaw: jobDoc.jobInfo.core.dateTimeFinish,
            Company: jobDoc.jobInfo.core.company,
            Location: jobDoc.jobInfo.core.location,
            Pay: jobDoc.jobInfo.core.pay,
            ShiftNotes: jobDoc.jobInfo.core.shiftNotes,
            NumJobSlots: jobDoc.jobInfo.core.numJobSlots,
            JobStatusSched: jobDoc.jobInfo.status.jobStatusSched,
            IsPaidJob: jobDoc.jobInfo.status.isPaidJob,
            PaidWerker: jobDoc.jobInfo.status.isPaidWerker,
            PaidWerkers: jobDoc.jobInfo.status.isPaidWerkers,
            JobCancelledByScheduler: jobDoc.jobInfo.status.jobCancelledByScheduler,
            CreatedAt: jobDoc.docStamp.timestamp.createdAt,
            UpdatedAt: jobDoc.docStamp.timestamp.updatedAt,
            IsRead: jobDoc.jobInfo.status.isRead,
            IsNewMessage: jobDoc.jobInfo.status.isNewMessage,
            WerkerFirstName: jobDoc.docStamp.userInfoTo.firstName,
            WerkerLastName: jobDoc.docStamp.userInfoTo.lastName,
            WerkerProfilePic: jobDoc.docStamp.userInfoTo.profilePicURL,
            WerkerStatus: jobDoc.jobInfo.status.jobStatusWerker,
            JobStatusWerker: jobDoc.jobInfo.status.jobStatusWerker,
            DocPath: docu.ref.path,
          });
        });

        setMyWerkersJob(werkerJobDocs);
      });

      return () => unsubscribe();
    } catch (err) {
      // console.log(err);
    }
  }, [jobDocId]);

  // Live listener for dummy werkers assigned to this job
  useEffect(() => {
    if (!jobDocId) return;
    const dummyColRef = collection(db, FB_COLLECTION_PATH.JobsDocs, jobDocId, "dummyWerkers");
    const unsubscribe = onSnapshot(dummyColRef, (snap) => {
      const docs: DummyWerkerData[] = [];
      snap.forEach((d) => {
        const data = d.data();
        docs.push({
          id: d.id,
          firstName: data.firstName ?? "Dummy",
          lastName: data.lastName ?? "Werker",
          createdAt: data.createdAt ?? 0,
        });
      });
      setDummyWerkersForJob(docs);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDocId]);

  // Live listener for scheduler's saved dummy names
  useEffect(() => {
    if (!usersID) return;
    const savedRef = collection(db, FB_COLLECTION_PATH.Users, usersID, FB_COLLECTION_PATH.SavedDummies);
    const unsubscribe = onSnapshot(savedRef, (snap) => {
      const docs: SavedDummyData[] = [];
      snap.forEach((d) => {
        const data = d.data();
        docs.push({
          id: d.id,
          firstName: data.firstName ?? "Dummy",
          lastName: data.lastName ?? "Werker",
          createdAt: data.createdAt ?? 0,
        });
      });
      setSavedDummies(docs.sort((a, b) => a.createdAt - b.createdAt));
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersID]);

  // -----------------------------
  // Derived data (memoized)
  // -----------------------------
  const scheduledList = useMemo(
    () =>
      myWerkersJob
        .filter(
          (w) => w.WerkerStatus === WORKFLOW_WERKER_TYPES.Scheduled || w.WerkerStatus === WORKFLOW_WERKER_TYPES.Past
        )
        .sort((a, b) => (a.UpdatedAt < b.UpdatedAt ? -1 : a.UpdatedAt > b.UpdatedAt ? 1 : 0)),
    [myWerkersJob]
  );

  const invitedList = useMemo(
    () =>
      myWerkersJob.filter(
        (w) => w.WerkerStatus === WORKFLOW_WERKER_TYPES.Invited || w.WerkerStatus === WORKFLOW_WERKER_TYPES.Expired
      ),
    [myWerkersJob]
  );

  const filledCount = scheduledList.length + dummyWerkersForJob.length;
  const totalSlots = currentSchedJob?.NumJobSlots ?? 0;
  const openShifts = Math.max(totalSlots - filledCount, 0);
  const isOverflow = !currentSchedJob?.JobCancelledByScheduler && filledCount > totalSlots;

  ////////////////////////////////////////////////////////////////////////////////
  const handleDecrementOpenSpotByOne = () => {
    const removeTheSlot = async () => {
      const RemoveOne = async () => {
        const jobStatusRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobDocId);
        await updateDoc(jobStatusRef, {
          "jobInfo.core.numJobSlots": increment(-1),
        });
        return { status: "sent" };
      };
      const RemoveOneAndFillJob = async () => {
        const jobStatusRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobDocId);
        await updateDoc(jobStatusRef, {
          "jobInfo.core.numJobSlots": increment(-1),
          "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Filled,
        });
        return { status: "sent" };
      };
      const expireWerkers = async () => {
        const unInviteRef = query(
          collectionGroup(db, "jobsDocs"),
          where("jobInfo.core.uniqueJobId", "==", jobDocId),
          where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Invited)
        );
        const querySnapshot = await getDocs(unInviteRef);
        querySnapshot.forEach((d) =>
          updateDoc(d.ref, {
            "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Expired,
          })
        );
      };

      if (filledCount + 1 === currentSchedJob.NumJobSlots) {
        await expireWerkers();
        await RemoveOneAndFillJob();
        await fx_FB_WerkerFeedUpdateAll(usersID, usersID, jobDocId, { action: "filled" });
      } else {
        await RemoveOne();
      }

      return { status: "sent" };
    };

    if (currentSchedJob.NumJobSlots === 1) {
      present({
        header: "You cannot remove the only Shift on a job.",
        buttons: ["Ok"],
      });
    } else {
      present({
        header: "Remove Open Shift?",
        buttons: ["Cancel", { text: "Ok", handler: removeTheSlot }],
      });
    }
  };

  const EmptyJobSlots: React.FC = () => {
    if (openShifts <= 0) return <></>;
    return (
      <IonList>
        {[...Array(openShifts)].map((_, i) => (
          <IonItem key={i}>
            <IonLabel>
              Open Shift
              <p>Shift # {i + 1 + filledCount}</p>
            </IonLabel>
            {currentSchedJob.JobCancelledByScheduler === false &&
              currentSchedJob.JobStatusSched !== WORKFLOW_SCHEDULER_TYPES.Past && (
                <IonIcon
                  slot="end"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAddWerkerModal(true);
                  }}
                  size="small"
                  color="medium"
                  icon={personAdd}
                  style={{ marginRight: 8 }}
                />
              )}
            <IonIcon
              slot="end"
              onClick={handleDecrementOpenSpotByOne}
              className="cancelbox"
              size="small"
              color="medium"
              icon={closeOutline}
            />
          </IonItem>
        ))}
      </IonList>
    );
  };

  const handleCancelWerker = (werkerDocId: string, werkerID: string) => {
    const overflowCount = filledCount;

    const cancelWerkerOperation = async () => {
      let removedWerkerBecameInvited = false;

      try {
        await presentLoading({
          cssClass: "custom-loading-transparent",
        });

        const jobDocRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobDocId);
        const werkerJobRef = doc(db, FB_COLLECTION_PATH.JobsDocs, werkerDocId);

        await ensureScheduledCountExists(jobDocRef, jobDocId);

        let shouldReInviteExpired = false;
        let shouldExpireInvited = false;

        await runTransaction(db, async (transaction) => {
          const [jobSnap, werkerSnap] = await Promise.all([transaction.get(jobDocRef), transaction.get(werkerJobRef)]);

          if (!jobSnap.exists()) throw new Error("Job no longer exists.");
          if (!werkerSnap.exists()) throw new Error("Werker record no longer exists.");

          const jobData: any = jobSnap.data();
          const werkerData: any = werkerSnap.data();

          const totalSlots = jobData.jobInfo.core.numJobSlots ?? 0;

          const scheduledCountRaw = jobData.jobInfo.status.scheduledCount;
          const scheduledCount =
            typeof scheduledCountRaw === "number" && Number.isFinite(scheduledCountRaw) ? scheduledCountRaw : 0;

          const currentWerkerStatus = werkerData.jobInfo.status.jobStatusWerker;

          const wasCounted =
            currentWerkerStatus === WORKFLOW_WERKER_TYPES.Scheduled ||
            currentWerkerStatus === WORKFLOW_WERKER_TYPES.Past;

          const newCount = wasCounted ? Math.max(scheduledCount - 1, 0) : scheduledCount;

          transaction.update(werkerJobRef, {
            "jobInfo.status.jobStatusWerker": jobDocId === werkerDocId ? "" : WORKFLOW_WERKER_TYPES.Invited,
            "docStamp.timestamp.updatedAt": Date.now(),
          });

          removedWerkerBecameInvited = jobDocId !== werkerDocId;

          if (wasCounted) {
            transaction.update(jobDocRef, {
              "jobInfo.status.scheduledCount": newCount,
            });
          }

          const currentSchedStatus = jobData.jobInfo.status.jobStatusSched;

          if (overflowCount > totalSlots) {
            shouldExpireInvited = true;
            transaction.update(jobDocRef, {
              ...(wasCounted ? { "jobInfo.status.scheduledCount": newCount } : {}),
              "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Filled,
            });
          } else if (
            currentSchedStatus === WORKFLOW_SCHEDULER_TYPES.Filled ||
            currentSchedStatus === WORKFLOW_SCHEDULER_TYPES.Past
          ) {
            shouldReInviteExpired = true;

            transaction.update(jobDocRef, {
              ...(wasCounted ? { "jobInfo.status.scheduledCount": newCount } : {}),
              "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Open,
            });
          }
        });

        fx_FB_SendNotification(usersID, [werkerID], PUSH_NOTIFICATION_TYPES.SchedulerRemovedWerkerFromShift, {
          schedFName: profile.FirstName,
          uniqueJobDocId: jobDocId,
          werkerJobDocId: werkerDocId,
        });

        if (removedWerkerBecameInvited) {
          await setFeedAvailableForWerker(werkerID, werkerDocId);
        }

        if (shouldReInviteExpired) {
          const reInviteRef = query(
            collectionGroup(db, "jobsDocs"),
            where("jobInfo.core.uniqueJobId", "==", jobDocId),
            where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Expired)
          );
          const qs = await getDocs(reInviteRef);

          for (const d of qs.docs) {
            const data: any = d.data();
            const uid = getUidFromWerkerJobDoc(data);

            await updateDoc(d.ref, {
              "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Invited,
            });

            if (uid) {
              await setFeedAvailableForWerker(uid, d.id);
            }
          }
        }

        if (shouldExpireInvited) {
          const expireInvitedRef = query(
            collectionGroup(db, "jobsDocs"),
            where("jobInfo.core.uniqueJobId", "==", jobDocId),
            where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Invited)
          );
          const qs = await getDocs(expireInvitedRef);
          await Promise.all(
            qs.docs.map((d) =>
              updateDoc(d.ref, {
                "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Expired,
              })
            )
          );

          await fx_FB_WerkerFeedUpdateAll(werkerID, usersID, jobDocId, { action: "filled" });
        }
      } catch (error: any) {
        presentToast({
          message: "Operation failed, please try again.",
          duration: 2500,
          position: "top",
          color: "danger",
        });
      } finally {
        await dismissLoading();
      }
    };

    if (currentSchedJob.JobStatusSched !== WORKFLOW_SCHEDULER_TYPES.Past) {
      present({
        header: "Cancel Werker?",
        buttons: [
          "No",
          {
            text: "Yes",
            handler: () => {
              cancelWerkerOperation();
              return true;
            },
          },
        ],
      });
    } else {
      present({
        header: "Job is already complete! Are you sure you want to cancel werker?",
        buttons: [
          "No",
          {
            text: "Yes, I'm sure",
            handler: () => {
              cancelWerkerOperation();
              return true;
            },
          },
        ],
      });
    }
  };

  const _handleCompleteWerker = (_werkerDocId: string) => {
    // intentionally left commented per your original
  };

  const handlePayWerker = (werker: WerkerJobData) => {
    if (werker.PaidWerker) return;

    const estimatedAmount = calculateJobPay(
      werker.Pay,
      werker.StartDateTimeRaw,
      werker.FinishDateTimeRaw,
      werker.ShiftNotes
    ).amount;

    const selectedJob: UnpaidJobData = {
      jobDocId: werker.MyJobDocId,
      jobDocPath: werker.DocPath,
      uniqueJobId: werker.JobDocId,
      company: werker.Company,
      dateTimeStart: werker.StartDateTimeRaw,
      dateTimeFinish: werker.FinishDateTimeRaw,
      pay: werker.Pay,
      notes: werker.ShiftNotes,
      estimatedAmount,
      selected: true,
      isCancelled: werker.JobCancelledByScheduler === true,
    };

    const werkerData: UnpaidWerkerSummary = {
      werkerUid: werker.WerkerUid,
      werkerName: `${werker.WerkerFirstName} ${werker.WerkerLastName}`.trim(),
      werkerProfilePic: werker.WerkerProfilePic,
      unpaidJobs: [selectedJob],
      totalUnpaid: estimatedAmount,
      jobCount: 1,
    };

    history.push({
      pathname: "/payments/summary",
      state: { werkerData, selectedJobs: [selectedJob] },
    });
  };

  const handleOpenReceipt = async (werker: WerkerJobData) => {
    if (!werker.PaidWerker) return;
    if (!usersID || !werker.WerkerUid) return;
    try {
      const receiptId = await findPaymentReceiptForJob(usersID, werker.WerkerUid, werker.JobDocId, werker.MyJobDocId);
      if (receiptId) {
        history.push(`/payments/receipt/${receiptId}`);
      }
    } catch {
      // Silent no-op per requirements
    }
  };

  const HandleUninviteWerker = (werkerDocId: string, werkerUid: string) => {
    const uninviteWerkerOperation = async () => {
      try {
        await presentLoading({
          cssClass: "custom-loading-transparent",
        });

        const jobStatusRef = doc(db, FB_COLLECTION_PATH.JobsDocs, werkerDocId);

        await updateDoc(jobStatusRef, {
          "jobInfo.status.jobStatusWerker": "",
        });

        await deleteFeedForWerker(werkerUid);
      } catch (error: any) {
        presentToast({
          message: "Operation failed, please try again.",
          duration: 2500,
          position: "top",
          color: "danger",
        });
      } finally {
        await dismissLoading();
      }
    };

    present({
      header: "Uninvite this werker?",
      buttons: [
        "Cancel",
        {
          text: "Ok",
          handler: () => {
            uninviteWerkerOperation();
            return true;
          },
        },
      ],
    });
  };

  const incrementNumberOfWerkersNeeded = async () => {
    const AddOne = async () => {
      const jobStatusRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobDocId);
      await updateDoc(jobStatusRef, {
        "jobInfo.core.numJobSlots": increment(1),
        "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Open,
      });
      return { status: "sent" };
    };
    const ReInviteWerker = async () => {
      const reInviteRef = query(
        collectionGroup(db, "jobsDocs"),
        where("jobInfo.core.uniqueJobId", "==", jobDocId),
        where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Expired)
      );
      const qs = await getDocs(reInviteRef);

      for (const d of qs.docs) {
        const data: any = d.data();
        const uid = getUidFromWerkerJobDoc(data);

        await updateDoc(d.ref, {
          "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Invited,
        });

        if (uid) {
          await setFeedAvailableForWerker(uid, d.id);
        }
      }
    };

    if (
      currentSchedJob.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Filled ||
      currentSchedJob.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Past
    ) {
      await ReInviteWerker();
      await AddOne();
    } else {
      await AddOne();
    }
    return { status: "sent" };
  };

  const handleAddThisWerkerToShift = (werkerDocId: any) => {
    const addWerkerOperation = async () => {
      try {
        await presentLoading({
          cssClass: "custom-loading-transparent",
        });

        const jobDocRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobDocId);
        const werkerJobRef = doc(db, FB_COLLECTION_PATH.JobsDocs, werkerDocId);

        await ensureScheduledCountExists(jobDocRef, jobDocId);

        let becameFilled = false;

        await runTransaction(db, async (transaction) => {
          const [jobSnap, werkerSnap] = await Promise.all([transaction.get(jobDocRef), transaction.get(werkerJobRef)]);

          if (!jobSnap.exists()) throw new Error("Job no longer exists.");
          if (!werkerSnap.exists()) throw new Error("Werker record no longer exists.");

          const jobData: any = jobSnap.data();
          const werkerData: any = werkerSnap.data();

          const totalSlots = jobData.jobInfo.core.numJobSlots ?? 0;

          const scheduledCountRaw = jobData.jobInfo.status.scheduledCount;
          const scheduledCount =
            typeof scheduledCountRaw === "number" && Number.isFinite(scheduledCountRaw) ? scheduledCountRaw : 0;

          if (scheduledCount >= totalSlots) {
            throw new Error("All slots are filled.");
          }

          const currentWerkerStatus = werkerData.jobInfo.status.jobStatusWerker;
          if (
            currentWerkerStatus === WORKFLOW_WERKER_TYPES.Scheduled ||
            currentWerkerStatus === WORKFLOW_WERKER_TYPES.Past
          ) {
            throw new Error("This werker is already scheduled.");
          }

          const newCount = scheduledCount + 1;
          becameFilled = totalSlots > 0 && newCount >= totalSlots;

          transaction.update(jobDocRef, {
            "jobInfo.status.scheduledCount": newCount,
            ...(becameFilled && {
              "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Filled,
            }),
          });

          transaction.update(werkerJobRef, {
            "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Scheduled,
            "docStamp.timestamp.updatedAt": Date.now(),
          });
        });

        if (becameFilled) {
          const expireRef = query(
            collectionGroup(db, "jobsDocs"),
            where("jobInfo.core.uniqueJobId", "==", jobDocId),
            where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Invited)
          );
          const qs = await getDocs(expireRef);
          await Promise.all(
            qs.docs.map((d) =>
              updateDoc(d.ref, {
                "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Expired,
              })
            )
          );
        }

        if (becameFilled) {
          const werkerDetails = myWerkersJob.find((w) => w.MyJobDocId === werkerDocId);
          const winnerUid = werkerDetails?.WerkerUid || usersID;
          await fx_FB_WerkerFeedUpdateAll(winnerUid, usersID, jobDocId, { action: "filled" });
        }

        const werkerDetails = myWerkersJob.find((w) => w.MyJobDocId === werkerDocId);
        if (werkerDetails?.WerkerUid) {
          await setFeedScheduledForWerker(werkerDetails.WerkerUid, werkerDocId);

          fx_FB_SendNotification(
            usersID,
            [werkerDetails.WerkerUid],
            PUSH_NOTIFICATION_TYPES.SchedulerAddedWerkerToShift,
            {
              schedFName: profile.FirstName,
              uniqueJobDocId: jobDocId,
              schedUid: usersID,
              werkerJobDocId: werkerDocId,
            }
          );
        }
      } catch (error: any) {
        presentToast({
          message: "Operation failed, please try again.",
          duration: 2500,
          position: "top",
          color: "danger",
        });
      } finally {
        await dismissLoading();
      }
    };

    present({
      header: "Add this Werker to the Shift?",
      buttons: [
        "Cancel",
        {
          text: "Ok",
          handler: () => {
            addWerkerOperation();
            return true;
          },
        },
      ],
    });
  };

  const handleDeleteSavedDummy = async (savedDummyId: string) => {
    try {
      await deleteDoc(doc(db, "users", usersID, FB_COLLECTION_PATH.SavedDummies, savedDummyId));
    } catch (e) {
      console.error("Error deleting saved dummy:", e);
    }
  };

  const handleAddDummyToShift = async (firstName: string, lastName: string, saveForLater: boolean) => {
    try {
      await presentLoading({ cssClass: "custom-loading-transparent" });

      const jobDocRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobDocId);
      await ensureScheduledCountExists(jobDocRef, jobDocId);

      let becameFilled = false;

      await runTransaction(db, async (transaction) => {
        const jobSnap = await transaction.get(jobDocRef);
        if (!jobSnap.exists()) throw new Error("Job no longer exists.");

        const jobData: any = jobSnap.data();
        const totalSlots = jobData.jobInfo.core.numJobSlots ?? 0;

        const scheduledCountRaw = jobData.jobInfo.status.scheduledCount;
        const scheduledCount =
          typeof scheduledCountRaw === "number" && Number.isFinite(scheduledCountRaw) ? scheduledCountRaw : 0;

        if (scheduledCount >= totalSlots) throw new Error("All slots are filled.");

        const newCount = scheduledCount + 1;
        becameFilled = totalSlots > 0 && newCount >= totalSlots;

        transaction.update(jobDocRef, {
          "jobInfo.status.scheduledCount": newCount,
          ...(becameFilled && {
            "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Filled,
          }),
        });
      });

      const dummyColRef = collection(db, FB_COLLECTION_PATH.JobsDocs, jobDocId, "dummyWerkers");
      await addDoc(dummyColRef, {
        firstName: firstName.trim() || "Dummy",
        lastName: lastName.trim() || "Werker",
        isDummy: true,
        createdAt: Date.now(),
      });

      if (saveForLater && (firstName.trim() || lastName.trim())) {
        const savedRef = collection(db, FB_COLLECTION_PATH.Users, usersID, FB_COLLECTION_PATH.SavedDummies);
        await addDoc(savedRef, {
          firstName: firstName.trim() || "Dummy",
          lastName: lastName.trim() || "Werker",
          createdAt: Date.now(),
        });
      }

      if (becameFilled) {
        const expireRef = query(
          collectionGroup(db, "jobsDocs"),
          where("jobInfo.core.uniqueJobId", "==", jobDocId),
          where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Invited)
        );
        const qs = await getDocs(expireRef);
        await Promise.all(
          qs.docs.map((d) => updateDoc(d.ref, { "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Expired }))
        );
        await fx_FB_WerkerFeedUpdateAll(usersID, usersID, jobDocId, { action: "filled" });
      }

      setShowDummyModal(false);
      setDummyFirstName("Dummy");
      setDummyLastName("Werker");
    } catch (error: any) {
      presentToast({
        message:
          error?.message === "All slots are filled." ? "All slots are filled." : "Operation failed, please try again.",
        duration: 2500,
        position: "top",
        color: "danger",
      });
    } finally {
      await dismissLoading();
    }
  };

  const handleRemoveDummyFromShift = (dummyDocId: string) => {
    const overflowCount = filledCount;

    const removeDummyOperation = async () => {
      try {
        await presentLoading({ cssClass: "custom-loading-transparent" });

        const jobDocRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobDocId);
        await ensureScheduledCountExists(jobDocRef, jobDocId);

        let shouldReInviteExpired = false;

        await runTransaction(db, async (transaction) => {
          const jobSnap = await transaction.get(jobDocRef);
          if (!jobSnap.exists()) throw new Error("Job no longer exists.");

          const jobData: any = jobSnap.data();
          const totalSlots = jobData.jobInfo.core.numJobSlots ?? 0;

          const scheduledCountRaw = jobData.jobInfo.status.scheduledCount;
          const scheduledCount =
            typeof scheduledCountRaw === "number" && Number.isFinite(scheduledCountRaw) ? scheduledCountRaw : 0;

          const newCount = Math.max(scheduledCount - 1, 0);
          const currentSchedStatus = jobData.jobInfo.status.jobStatusSched;

          if (overflowCount > totalSlots) {
            transaction.update(jobDocRef, {
              "jobInfo.status.scheduledCount": newCount,
              "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Filled,
            });
          } else if (
            currentSchedStatus === WORKFLOW_SCHEDULER_TYPES.Filled ||
            currentSchedStatus === WORKFLOW_SCHEDULER_TYPES.Past
          ) {
            shouldReInviteExpired = true;
            transaction.update(jobDocRef, {
              "jobInfo.status.scheduledCount": newCount,
              "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Open,
            });
          } else {
            transaction.update(jobDocRef, {
              "jobInfo.status.scheduledCount": newCount,
            });
          }
        });

        const dummyDocRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobDocId, "dummyWerkers", dummyDocId);
        await deleteDoc(dummyDocRef);

        if (shouldReInviteExpired) {
          const reInviteRef = query(
            collectionGroup(db, "jobsDocs"),
            where("jobInfo.core.uniqueJobId", "==", jobDocId),
            where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Expired)
          );
          const qs = await getDocs(reInviteRef);
          for (const d of qs.docs) {
            const data: any = d.data();
            const uid = getUidFromWerkerJobDoc(data);
            await updateDoc(d.ref, {
              "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Invited,
            });
            if (uid) {
              await setFeedAvailableForWerker(uid, d.id);
            }
          }
        }
      } catch (error: any) {
        presentToast({
          message: "Operation failed, please try again.",
          duration: 2500,
          position: "top",
          color: "danger",
        });
      } finally {
        await dismissLoading();
      }
    };

    present({
      header: "Remove dummy werker from shift?",
      buttons: [
        "Cancel",
        {
          text: "Yes",
          handler: () => {
            removeDummyOperation();
            return true;
          },
        },
      ],
    });
  };

  const addSelfToJob = async () => {
    try {
      await presentLoading({
        cssClass: "custom-loading-transparent",
      });

      const jobDocRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobDocId);
      await ensureScheduledCountExists(jobDocRef, jobDocId);

      let becameFilled = false;

      await runTransaction(db, async (transaction) => {
        const jobSnap = await transaction.get(jobDocRef);
        if (!jobSnap.exists()) {
          throw new Error("Job no longer exists.");
        }
        const jobData: any = jobSnap.data();

        const totalSlots = jobData.jobInfo.core.numJobSlots ?? 0;

        const scheduledCountRaw = jobData.jobInfo.status.scheduledCount;
        const scheduledCount =
          typeof scheduledCountRaw === "number" && Number.isFinite(scheduledCountRaw) ? scheduledCountRaw : 0;

        if (scheduledCount >= totalSlots) {
          throw new Error("All slots are filled.");
        }

        const currentWerkerStatus = jobData.jobInfo.status.jobStatusWerker;
        if (
          currentWerkerStatus === WORKFLOW_WERKER_TYPES.Scheduled ||
          currentWerkerStatus === WORKFLOW_WERKER_TYPES.Past
        ) {
          throw new Error("You are already scheduled for this job.");
        }

        const newCount = scheduledCount + 1;
        becameFilled = totalSlots > 0 && newCount >= totalSlots;

        transaction.update(jobDocRef, {
          "jobInfo.status.scheduledCount": newCount,
          "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Scheduled,
          ...(becameFilled && {
            "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Filled,
          }),
        });
      });

      if (becameFilled) {
        const expireRef = query(
          collectionGroup(db, "jobsDocs"),
          where("jobInfo.core.uniqueJobId", "==", jobDocId),
          where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Invited)
        );
        const qs = await getDocs(expireRef);
        await Promise.all(
          qs.docs.map((d) =>
            updateDoc(d.ref, {
              "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Expired,
            })
          )
        );
        await fx_FB_WerkerFeedUpdateAll(usersID, usersID, jobDocId, { action: "filled" });
      }
    } catch (error: any) {
      presentToast({
        message: "Operation failed, please try again.",
        duration: 2500,
        position: "top",
        color: "danger",
      });
    } finally {
      await dismissLoading();
    }
  };

  const EditInvitedWerkers = () => {
    const goEditThisJob = () => {
      badgeDispatch({ type: "CHANGE_EDIT_WERKERS", payload: "yes" });
      badgeDispatch({
        type: VIEW_TYPES.CHANGE_INVITED_WERKERS,
        payload: InvitedWerkersList,
      });
      badgeDispatch({
        type: "CHANGE_SCHEDULED_WERKERS",
        payload: ScheduledWerkersList,
      });
      if (onEditWerkersRequest) {
        onEditWerkersRequest({ nextView: "selectWerkers", jobId: jobDocId });
      } else if (profile.SubscriptionType !== "Scheduler") {
        history.push({
          pathname: "/Paywall",
          state: {
            cameFrom: "ScheduleJob",
            postCreate: { jobId: jobDocId, hasAssociates: true },
          },
        });
      } else {
        history.push("/SelectWerkers/" + jobDocId);
      }
    };

    const goEditWholeSeries = () => {
      if (!seriesId) return;
      if (onEditWerkersRequest) {
        onEditWerkersRequest({ nextView: "selectWerkersSeries", seriesId });
      } else if (profile.SubscriptionType !== "Scheduler") {
        history.push({
          pathname: "/Paywall",
          state: {
            cameFrom: "ScheduleJob",
            postCreate: {
              jobId: jobDocId,
              seriesId,
              hasAssociates: true,
              cameFromDetail: true,
            },
          },
        });
      } else {
        history.push({
          pathname: "/SelectWerkersSeries/" + seriesId,
          state: { cameFromDetail: true },
        });
      }
    };

    if (currentSchedJob.NumJobSlots === 0) {
      present({
        header: "Add a shift above if you want to invite werkers.",
        buttons: [{ text: "Ok" }],
      });
      return;
    }

    if (seriesId) {
      present({
        header: "Edit Invited",
        message: "Do you want to edit invited werkers for just this job, or the whole series?",
        buttons: [
          "Cancel",
          { text: "This Job Only", handler: goEditThisJob },
          { text: "Whole Series", handler: goEditWholeSeries },
        ],
      });
      return;
    }

    present({
      header: "Edit Invited Werkers?",
      buttons: ["Cancel", { text: "Ok", handler: goEditThisJob }],
    });
  };

  ////////////////////////////////////////////////////////////////////////////////
  // ✅ UI: Scheduled section (NOT accordion)
  const ScheduledWerkersList: React.FC = () => {
    if (currentSchedJob.NumJobSlots === 0) {
      return (
        <IonList>
          <IonItem key={0}>
            <IonLabel className="labelo">There are no Shifts on this job.</IonLabel>
          </IonItem>
        </IonList>
      );
    }

    if (myWerkersJob.length === 0 && dummyWerkersForJob.length === 0) {
      return <EmptyJobSlots />;
    }

    return (
      <>
        {/* Section header */}
        <IonItem lines="full">
          <IonLabel>
            <strong>Scheduled</strong>
            <p style={{ marginTop: 4, opacity: 0.8 }}>
              {currentSchedJob?.JobCancelledByScheduler ? "Job cancelled" : `${filledCount}/${totalSlots} filled`}
            </p>
          </IonLabel>

          {/* Right-side actions + count */}
          <div slot="end" style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {/* Add Self */}
            {currentSchedJob.JobStatusWerker === "" && (
              <IonButton
                onClick={() =>
                  present({
                    header: "Add Self to Job?",
                    buttons: [
                      "Cancel",
                      {
                        text: "Ok",
                        handler: () => {
                          addSelfToJob();
                          return true;
                        },
                      },
                    ],
                  })
                }
                size="small"
                color="medium"
                fill="outline"
                disabled={
                  currentSchedJob.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Filled ||
                  currentSchedJob.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Past
                }
              >
                Add Self
              </IonButton>
            )}

            {/* Add Shift + */}
            {currentSchedJob.JobCancelledByScheduler === false && (
              <IonButton
                onClick={() =>
                  present({
                    header: "Add Another Slot?",
                    buttons: ["Cancel", { text: "Ok", handler: incrementNumberOfWerkersNeeded }],
                  })
                }
                color="medium"
                fill="solid"
                size="small"
                disabled={currentSchedJob.PaidWerkers === true}
              >
                Add Shift +
              </IonButton>
            )}
          </div>
        </IonItem>

        {/* Overflow warning (kept, but now sits above the list content) */}
        {isOverflow && !currentSchedJob.JobCancelledByScheduler && (
          <IonList className="excessWerkers">
            <IonItem lines="none">
              <IonLabel>
                <strong>An error occurred.</strong>
                <p>{`Job Slots: ${totalSlots}`}</p>
                <p>{`Scheduled Werkers: ${filledCount}`}</p>
                <p>Please remove a werker or increase number of job slots</p>
              </IonLabel>
            </IonItem>
          </IonList>
        )}

        {/* Scheduled list */}
        <IonList className={isOverflow && !currentSchedJob.JobCancelledByScheduler ? "excessWerkers" : ""}>
          {scheduledList.map((werker) => (
            <IonItem key={werker.WerkerUid}>
              <IonAvatar className="avatario" style={{ height: "30px", width: "30px" }} slot="start">
                <img src={werker.WerkerProfilePic} alt="" />
              </IonAvatar>

              <IonLabel>
                {werker.WerkerFirstName} {werker.WerkerLastName}
              </IonLabel>

              {werker.WerkerStatus === WORKFLOW_WERKER_TYPES.Past &&
                werker.JobCancelledByScheduler === false &&
                !werker.PaidWerker && (
                  <IonIcon
                    onClick={() => handlePayWerker(werker)}
                    className="cancelbox"
                    color="medium"
                    size="small"
                    icon={cashOutline}
                  />
                )}

              {werker.JobCancelledByScheduler === false ? (
                <IonIcon
                  onClick={() => {
                    if (werker.PaidWerker) {
                      handleOpenReceipt(werker);
                    } else {
                      handleCancelWerker(werker.MyJobDocId, werker.WerkerUid);
                    }
                  }}
                  className="cancelbox"
                  slot="end"
                  color={werker.PaidWerker ? "success" : "medium"}
                  size="small"
                  icon={werker.PaidWerker ? cashOutline : closeOutline}
                />
              ) : (
                <IonIcon
                  onClick={() => {
                    if (werker.PaidWerker) {
                      handleOpenReceipt(werker);
                    }
                  }}
                  className="cancelbox"
                  slot="end"
                  color={werker.PaidWerker ? "success" : "danger"}
                  size="large"
                  icon={werker.PaidWerker ? cashOutline : undefined}
                />
              )}
            </IonItem>
          ))}
          {dummyWerkersForJob.map((dummy) => (
            <IonItem key={`dummy-${dummy.id}`}>
              <IonIcon
                slot="start"
                icon={helpCircleOutline}
                style={{ fontSize: "30px", color: "#888", marginRight: 8 }}
              />
              <IonLabel>
                {dummy.firstName} {dummy.lastName}
              </IonLabel>
              {currentSchedJob.JobCancelledByScheduler === false && (
                <IonIcon
                  onClick={() => handleRemoveDummyFromShift(dummy.id)}
                  className="cancelbox"
                  slot="end"
                  color="medium"
                  size="small"
                  icon={closeOutline}
                />
              )}
            </IonItem>
          ))}
        </IonList>

        {/* Open shifts at bottom */}
        {filledCount < totalSlots && <EmptyJobSlots />}
      </>
    );
  };

  ////////////////////////////////////////////////////////////////////////////////
  // ✅ UI: Invited panel (collapsed by default) ABOVE Scheduled
  const InvitedWerkersList: React.FC = () => {
    const hasNoInvites = invitedList.length === 0;

    return (
      <>
        {/* Panel header row (always visible) */}
        <IonItem button={true} detail={false} lines="full" onClick={() => setInvitedOpen((v) => !v)}>
          <IonIcon slot="start" icon={invitedOpen ? chevronDownOutline : chevronForwardOutline} />

          <IonLabel>Invited ({invitedList.length})</IonLabel>

          {/* Always reachable: Edit button lives in the header row */}
          {currentSchedJob.JobCancelledByScheduler === false && (
            <IonButton
              slot="end"
              size="small"
              color="medium"
              fill="solid"
              disabled={
                currentSchedJob.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Filled ||
                currentSchedJob.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Past
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation(); // ✅ don’t toggle invitedOpen
                EditInvitedWerkers();
              }}
            >
              Edit Invited +/-
            </IonButton>
          )}
        </IonItem>

        {/* Collapsed content */}
        {invitedOpen && (
          <>
            {hasNoInvites ? (
              <IonList>
                <IonItem key={0}>
                  <IonLabel className="labelo">No Invited Werkers</IonLabel>
                </IonItem>
              </IonList>
            ) : (
              <IonList>
                {invitedList.map((invitedWerker) => (
                  <IonItem key={invitedWerker.WerkerUid}>
                    <IonAvatar className="avatario" style={{ height: "30px", width: "30px" }} slot="start">
                      <img src={invitedWerker.WerkerProfilePic} alt="" />
                    </IonAvatar>

                    <IonLabel className="labelo">
                      {invitedWerker.WerkerFirstName} {invitedWerker.WerkerLastName}
                    </IonLabel>

                    {currentSchedJob.PaidWerkers !== true &&
                      currentSchedJob.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Open && (
                        <IonIcon
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleAddThisWerkerToShift(invitedWerker.MyJobDocId);
                          }}
                          size="small"
                          color="medium"
                          icon={personAdd}
                        />
                      )}

                    {currentSchedJob.PaidWerkers !== true && (
                      <IonIcon
                        slot="end"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          HandleUninviteWerker(invitedWerker.MyJobDocId, invitedWerker.WerkerUid);
                        }}
                        className="cancelbox"
                        color={"medium"}
                        size="small"
                        icon={closeOutline}
                      />
                    )}
                  </IonItem>
                ))}
              </IonList>
            )}
          </>
        )}
      </>
    );
  };

  ////////////////////////////////////////////////////////////////////////////////

  if (!jobReady) {
    return (
      <React.Fragment>
        <IonRow>
          <IonCol>
            <IonList>
              <IonItem>
                <IonLabel>Loading werkers...</IonLabel>
              </IonItem>
            </IonList>
          </IonCol>
        </IonRow>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {/* Job cancelled row */}
      <IonRow>
        {currentSchedJob.JobCancelledByScheduler === false ? (
          <></>
        ) : (
          <IonCol>
            <h4>Job Cancelled:</h4>
          </IonCol>
        )}
      </IonRow>

      <InvitedWerkersList />

      <br />

      <ScheduledWerkersList />

      <br />
      <br />

      {/* Add Werker to Shift Modal */}
      <IonModal
        isOpen={showAddWerkerModal}
        onDidDismiss={() => setShowAddWerkerModal(false)}
        breakpoints={[0, 1]}
        initialBreakpoint={1}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>Add to Shift</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowAddWerkerModal(false)}>Cancel</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent scrollY={true}>
          <IonList>
            {/* Dummy Werker option — always first */}
            <IonItem
              button
              detail={false}
              onClick={() => {
                setShowAddWerkerModal(false);
                setDummyFirstName("Dummy");
                setDummyLastName("Werker");
                setShowDummyModal(true);
              }}
            >
              <IonIcon
                slot="start"
                icon={helpCircleOutline}
                style={{ fontSize: "30px", color: "#888", marginRight: 8 }}
              />
              <IonLabel>
                <strong>Dummy Werker</strong>
                <p>Add a non-WerkApp </p>
              </IonLabel>

              <IonButton
                slot="end"
                size="small"
                color="secondwarning"
                fill="solid"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddWerkerModal(false);
                  setDummyFirstName("Dummy");
                  setDummyLastName("Werker");
                  setShowDummyModal(true);
                }}
              >
                <IonIcon slot="start" size="small" icon={addOutline} />
                Add
              </IonButton>
            </IonItem>

            {/* Invited werkers */}
            {invitedList.length > 0 && (
              <>
                <IonItem lines="none">
                  <IonLabel>
                    <p
                      style={{
                        fontWeight: 600,
                        textTransform: "uppercase",
                        fontSize: "0.75rem",
                        opacity: 0.55,
                      }}
                    >
                      Invited Werkers
                    </p>
                  </IonLabel>
                </IonItem>

                {invitedList.map((invitedWerker) => (
                  <IonItem
                    key={invitedWerker.WerkerUid}
                    button
                    detail={false}
                    onClick={() => {
                      setShowAddWerkerModal(false);
                      handleAddThisWerkerToShift(invitedWerker.MyJobDocId);
                    }}
                  >
                    <IonAvatar className="avatario" style={{ height: "30px", width: "30px" }} slot="start">
                      <img src={invitedWerker.WerkerProfilePic} alt="" />
                    </IonAvatar>

                    <IonLabel className="labelo">
                      {invitedWerker.WerkerFirstName} {invitedWerker.WerkerLastName}
                    </IonLabel>
                  </IonItem>
                ))}
              </>
            )}
          </IonList>
        </IonContent>
      </IonModal>

      {/* Add Dummy Werker Modal */}
      <IonModal
        isOpen={showDummyModal}
        onDidDismiss={() => {
          setShowDummyModal(false);
          setDummyFirstName("Dummy");
          setDummyLastName("Werker");
        }}
        breakpoints={[0, 1]}
        initialBreakpoint={1}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>Add Dummy Werker</IonTitle>
            <IonButtons slot="start">
              <IonButton onClick={() => setShowDummyModal(false)}>Cancel</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonList>
            <IonItem lines="none">
              <IonLabel>
                <p style={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.75rem", opacity: 0.55 }}>Name</p>
              </IonLabel>
            </IonItem>
            <IonItem>
              <IonInput
                placeholder="First Name"
                value={dummyFirstName}
                onIonChange={(e: any) => setDummyFirstName(e.target.value ?? "")}
                clearInput
              />
            </IonItem>
            <IonItem>
              <IonInput
                placeholder="Last Name"
                value={dummyLastName}
                onIonChange={(e: any) => setDummyLastName(e.target.value ?? "")}
                clearInput
              />
            </IonItem>
          </IonList>

          {savedDummies.length > 0 && (
            <IonList>
              <IonItem lines="none">
                <IonLabel>
                  <p style={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.75rem", opacity: 0.55 }}>
                    Saved
                  </p>
                </IonLabel>
              </IonItem>
              {savedDummies.map((sd) => (
                <IonItem
                  key={sd.id}
                  button
                  detail={false}
                  onClick={() => {
                    setDummyFirstName(sd.firstName);
                    setDummyLastName(sd.lastName);
                  }}
                >
                  <IonIcon
                    slot="start"
                    icon={helpCircleOutline}
                    style={{ fontSize: "28px", color: "#888", marginRight: 8 }}
                  />
                  <IonLabel>
                    {sd.firstName} {sd.lastName}
                  </IonLabel>
                  <IonButton
                    slot="end"
                    fill="clear"
                    color="danger"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSavedDummy(sd.id);
                    }}
                  >
                    <IonIcon icon={trashOutline} />
                  </IonButton>
                </IonItem>
              ))}
            </IonList>
          )}
        </IonContent>
        <IonFooter>
          <IonToolbar style={{ padding: "8px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            <IonButton
              expand="block"
              color="medium"
              fill="solid"
              onClick={() => handleAddDummyToShift(dummyFirstName, dummyLastName, false)}
            >
              Add Without Saving
            </IonButton>
            <IonButton
              expand="block"
              color="secondwarning"
              fill="solid"
              onClick={() => handleAddDummyToShift(dummyFirstName, dummyLastName, true)}
            >
              <IonIcon slot="start" icon={saveOutline} />
              Save &amp; Add
            </IonButton>
          </IonToolbar>
        </IonFooter>
      </IonModal>
    </React.Fragment>
  );
};
